
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Rotate3dIcon, SparklesIcon, BadgeCheckIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import { UserProfile } from '../types';
import { analyzeFitMap } from '../services/geminiService';
import Spinner from './Spinner';

interface ThreeSixtyViewerProps {
    images: string[];
    initialIndex?: number;
    userProfile?: UserProfile;
}

const ThreeSixtyViewer: React.FC<ThreeSixtyViewerProps> = ({ images, initialIndex = 0, userProfile }) => {
    const { t } = useTranslation();
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const [showHint, setShowHint] = useState(true);
    const [isFitMapActive, setIsFitMapActive] = useState(false);
    const [isLoadingFitMap, setIsLoadingFitMap] = useState(false);
    const [fitMapData, setFitMapData] = useState<{ name: string, status: 'tight' | 'perfect' | 'loose', x: number, y: number }[]>([]);

    const handleToggleFitMap = async () => {
        if (isFitMapActive) {
            setIsFitMapActive(false);
            return;
        }

        setIsFitMapActive(true);
        if (fitMapData.length === 0 && userProfile) {
            setIsLoadingFitMap(true);
            try {
                const result = await analyzeFitMap(images[currentIndex], userProfile);
                setFitMapData(result.areas);
            } catch (e) {
                console.error(e);
            } finally {
                setIsLoadingFitMap(false);
            }
        }
    };

    // Hide hint after interaction
    useEffect(() => {
        if (isDragging) {
            setShowHint(false);
        }
    }, [isDragging]);

    const handleStart = (clientX: number) => {
        setIsDragging(true);
        setStartX(clientX);
    };

    const handleMove = (clientX: number) => {
        if (!isDragging || !containerRef.current) return;

        const deltaX = clientX - startX;
        const sensitivity = 20; // Pixels per frame switch

        if (Math.abs(deltaX) > sensitivity) {
            const direction = deltaX > 0 ? -1 : 1; // Drag right -> rotate left (prev image)
            const newIndex = (currentIndex + direction + images.length) % images.length;
            setCurrentIndex(newIndex);
            setStartX(clientX); // Reset start X for continuous rotation
        }
    };

    const handleEnd = () => {
        setIsDragging(false);
    };

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing select-none overflow-hidden"
            onMouseDown={(e) => handleStart(e.clientX)}
            onMouseMove={(e) => handleMove(e.clientX)}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
            onTouchStart={(e) => handleStart(e.touches[0].clientX)}
            onTouchMove={(e) => handleMove(e.touches[0].clientX)}
            onTouchEnd={handleEnd}
        >
            {/* Images */}
            <div className="relative w-full h-full flex items-center justify-center">
                <AnimatePresence mode='popLayout' initial={false}>
                    <motion.img
                        key={currentIndex}
                        src={images[currentIndex]}
                        alt={`360 view frame ${currentIndex}`}
                        initial={{ opacity: 0.8 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0.8 }}
                        transition={{ duration: 0.15 }}
                        className="max-w-full max-h-full object-contain pointer-events-none"
                        draggable={false}
                    />
                </AnimatePresence>
            </div>

            {/* Overlay Hint */}
            <AnimatePresence>
                {showHint && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/20"
                    >
                        <div className="bg-black/60 backdrop-blur-sm text-white px-4 py-2 rounded-full flex items-center gap-2">
                            <Rotate3dIcon className="w-6 h-6 animate-spin-slow" />
                            <span className="text-sm font-semibold">{t('threeSixty_dragToRotate')}</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* 360 Badge */}
            <div className="absolute top-4 right-4 bg-indigo-600 text-white text-xs font-bold px-2 py-1 rounded shadow-lg pointer-events-none">
                360° VIEW
            </div>

            {/* Fit Map Overlay */}
            <AnimatePresence>
                {isFitMapActive && (
                    <div className="absolute inset-0 pointer-events-none z-10">
                        {isLoadingFitMap ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm">
                                <Spinner className="w-8 h-8 text-white" />
                            </div>
                        ) : (
                            fitMapData.map((area, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0, opacity: 0 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className={`absolute px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1.5 backdrop-blur-md border ${area.status === 'perfect'
                                            ? 'bg-green-500/80 text-white border-green-400'
                                            : area.status === 'tight'
                                                ? 'bg-red-500/80 text-white border-red-400'
                                                : 'bg-amber-500/80 text-white border-amber-400'
                                        }`}
                                    style={{
                                        left: `${area.x}%`,
                                        top: `${area.y}%`,
                                        transform: 'translate(-50%, -50%)'
                                    }}
                                >
                                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                                    {area.name}: {area.status.toUpperCase()}
                                </motion.div>
                            ))
                        )}
                    </div>
                )}
            </AnimatePresence>

            {/* Tools */}
            <div className="absolute top-4 left-4 flex flex-col gap-2">
                <button
                    onClick={handleToggleFitMap}
                    disabled={!userProfile}
                    className={`p-2 rounded-lg shadow-lg flex items-center gap-2 transition-all ${isFitMapActive ? 'bg-indigo-600 text-white' : 'bg-white/90 text-gray-800 hover:bg-white'}`}
                    title="Toggle Fit Map"
                >
                    <BadgeCheckIcon className="w-5 h-5" />
                    {isFitMapActive && <span className="text-xs font-bold">Fit Analysis</span>}
                </button>
            </div>

            {/* Simple Indicator Dots */}
            <div className="absolute bottom-4 flex gap-2 pointer-events-none">
                {images.map((_, idx) => (
                    <div
                        key={idx}
                        className={`w-2 h-2 rounded-full transition-colors ${idx === currentIndex ? 'bg-indigo-500' : 'bg-gray-300/50'}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default ThreeSixtyViewer;
